import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import type { GridColDef } from "@mui/x-data-grid/models/colDef/gridColDef";
import { useTranslation } from "react-i18next";
import apiClient from "../utils/apiClient";

type AgentCpaClientsProps = {
  agentTnId: string;
  month: string;
  year: string;
};

type CpaClientRow = {
  id: number;
  fullname: string;
};

const AgentCpaClients = ({ agentTnId, month, year }: AgentCpaClientsProps) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<CpaClientRow[]>([]);

  const columns = useMemo<GridColDef<CpaClientRow>[]>(
    () => [
      {
        field: "fullname",
        headerName: t("agentCpaClients.columns.fio"),
        flex: 1,
        minWidth: 260,
      },
    ],
    [t],
  );

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await apiClient.get(
          `/detailing-cpa?month=${month}&year=${year}`,
          {
            params: { agent_tn_id: agentTnId },
          },
        );

        if (response.data?.success) {
          const clientsRaw = response.data.clients ?? [];
          const clients = Array.isArray(clientsRaw)
            ? clientsRaw
            : typeof clientsRaw === "string"
              ? JSON.parse(clientsRaw)
              : [];

          setRows(
            clients
              .filter(
                (c: { initials?: string }) =>
                  String(c?.initials ?? "").trim().length > 0,
              )
              .map((c: { initials?: string }, idx: number) => ({
                id: idx + 1,
                fullname: String(c.initials ?? ""),
              })),
          );
        }
      } catch (error) {
        console.error("Ошибка загрузки CPA-детализации клиентов", error);
      } finally {
        setLoading(false);
      }
    };

    if (agentTnId) {
      fetchData();
    }
  }, [agentTnId, month, year]);

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        px: 2,
        pb: 2,
      }}
    >
      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        pagination
        sx={{
          "--DataGrid-rowBorderColor": "#D1D3D4",
          "& .MuiDataGrid-columnHeaderTitleContainer": {
            fontSize: 12,
            lineHeight: "110%",
          },
          "& .MuiDataGrid-cell": {
            fontSize: 16,
            borderTop: "0.5px solid #D1D3D4",
          },
          fontFamily: "inherit",
          borderWidth: 0,
          background: "#ffffff",
          flex: 1,
        }}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
      />
    </Box>
  );
};

export default AgentCpaClients;
